import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useRecoilValue } from "recoil"
import { userAtom } from "@/atoms/userAtom"
import { Checkbox, Divider, Form, Input, Switch, Typography } from "antd"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import { useOrg } from "@/hooks/useOrg"
import { useFlags } from "flagsmith/react"

const { Title } = Typography

interface TrackingConfigFormProps {}

const TrackingConfigForm: React.FC<TrackingConfigFormProps> = () => {
  const { t } = useTranslation()
  const { updateOrgPreferences, isLoading } = useOrg()
  const { preference } = useRecoilValue(userAtom)
  const [trackingForm] = Form.useForm()
  const { trackingConfig } = preference
  const flags = useFlags(["show_tracking_config"])
  useEffect(() => {
    trackingForm.setFieldsValue({
      showDriverPosition: trackingConfig?.showDriverPosition,
      showDriverPhone: trackingConfig?.showDriverPhone,
      showPickupETAs: trackingConfig?.showPickupETAs,
      showDropoffETAs: trackingConfig?.showDropoffETAs,
      showDriverAssignedETAs: trackingConfig?.showDriverAssignedETAs,
      supportTrackingURL: trackingConfig?.supportTrackingURL,
      supportTrackingEmail: trackingConfig?.supportTrackingEmail,
      supportTrackingPhone: trackingConfig?.supportTrackingPhone,
      supportTrackingWhatsapp: trackingConfig?.supportTrackingWhatsapp,
    })
  }, [trackingConfig])

  const currentShowDriverPosition = Form.useWatch("showDriverPosition", trackingForm)
  const currentShowDriverPhone = Form.useWatch("showDriverPhone", trackingForm)
  const currentDropoffEta = Form.useWatch("showDropoffETAs", trackingForm)
  const currentPickupEta = Form.useWatch("showPickupETAs", trackingForm)
  const currentDriverAssignedEta = Form.useWatch("showDriverAssignedETAs", trackingForm)
  const currentSupportTrackingURL = Form.useWatch("supportTrackingURL", trackingForm)
  const currentSupportTrackingEmail = Form.useWatch("supportTrackingEmail", trackingForm)
  const currentSupportTrackingPhone = Form.useWatch("supportTrackingPhone", trackingForm)
  const currentSupportTrackingWhatsapp = Form.useWatch("supportTrackingWhatsapp", trackingForm)

  const onFinishTracking = async (formValues: any) => {
    try {
      await updateOrgPreferences({
        ...preference,
        trackingConfig: {
          showDropoffETAs: formValues?.showDropoffETAs,
          showPickupETAs: formValues?.showPickupETAs,
          showDriverAssignedETAs: formValues?.showDriverAssignedETAs,
          showDriverPhone: formValues?.showDriverPhone,
          showDriverPosition: formValues?.showDriverPosition,
          supportTrackingEmail: formValues?.supportTrackingEmail,
          supportTrackingPhone: formValues?.supportTrackingPhone,
          supportTrackingURL: formValues?.supportTrackingURL,
          supportTrackingWhatsapp: formValues?.supportTrackingWhatsapp,
        },
      })
    } catch (error) {}
  }

  const isTrackingConfigModified =
    currentShowDriverPosition !== trackingConfig?.showDriverPosition ||
    currentShowDriverPhone !== trackingConfig?.showDriverPhone ||
    currentDropoffEta !== trackingConfig?.showDropoffETAs ||
    currentPickupEta !== trackingConfig?.showPickupETAs ||
    currentDriverAssignedEta !== trackingConfig?.showDriverAssignedETAs ||
    currentSupportTrackingURL !== trackingConfig?.supportTrackingURL ||
    currentSupportTrackingEmail !== trackingConfig?.supportTrackingEmail ||
    currentSupportTrackingPhone !== trackingConfig?.supportTrackingPhone ||
    currentSupportTrackingWhatsapp !== trackingConfig?.supportTrackingWhatsapp

  return (
    <>
      <Form
        id="brand-settings-form"
        form={trackingForm}
        colon={false}
        className="gap-y-4"
        validateTrigger="onSubmit"
        labelCol={{ span: 6 }}
        labelAlign="left"
        wrapperCol={{ span: 14 }}
        requiredMark={false}
        onFinish={onFinishTracking}
      >
        <Title level={5}>{t("common.tracking")}</Title>
        <Form.Item
          name="showDriverPosition"
          label={
            <span className="flex gap-x-2 font-semibold items-center">
              {t("common.showDriverLocation")}
            </span>
          }
          className="items-center"
          wrapperCol={{ span: 2 }}
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="showDriverPhone"
          label={
            <span className="flex gap-x-2 font-semibold items-center">
              {t("common.shareDriverPhone")}
            </span>
          }
          className="items-center"
          wrapperCol={{ span: 2 }}
        >
          <Switch />
        </Form.Item>
        <Form.Item name="showPickupETAs" className="hidden" />
        <Form.Item name="showDriverAssignedETAs" className="hidden" />
        <Form.Item name="showDropoffETAs" className="hidden" />
        {flags?.show_tracking_config?.enabled && (
          <Form.Item
            name="etas"
            label={
              <span className="flex gap-x-2 font-semibold items-center">{t("common.ETAs")}</span>
            }
            className="items-center"
            wrapperCol={{ span: 14 }}
          >
            <div className="flex flex-row w-full justify-between">
              <Checkbox
                checked={currentDriverAssignedEta}
                onChange={(e) => {
                  trackingForm.setFieldValue("showDriverAssignedETAs", e.target?.checked)
                }}
              >
                {t("common.driverAssignedTimes")}
              </Checkbox>
              <Checkbox
                checked={currentPickupEta}
                onChange={(e) => {
                  trackingForm.setFieldValue("showPickupETAs", e.target?.checked)
                }}
              >
                {t("common.pickupTimes")}
              </Checkbox>
              <Checkbox
                checked={currentDropoffEta}
                onChange={(e) => {
                  trackingForm.setFieldValue("showDropoffETAs", e.target?.checked)
                }}
              >
                {t("common.dropoffTimes")}
              </Checkbox>
            </div>
          </Form.Item>
        )}
        <div>
          <Divider />
          <div className="flex gap-x-2 items-center mb-4">
            <p>{t("common.tracking.supportDescription")}</p>
          </div>
          <Form.Item
            name="supportTrackingURL"
            label={<span className="font-semibold">{t("common.trackingSupportUrl")}</span>}
          >
            <Input placeholder={t("common.trackingSupportUrl")} type="text" />
          </Form.Item>
          <Form.Item
            name="supportTrackingEmail"
            rules={[{ type: "email" }]}
            label={<span className="font-semibold">{t("common.trackingSupportEmail")}</span>}
          >
            <Input placeholder={t("common.trackingSupportEmail")} type="text" />
          </Form.Item>
          <Form.Item
            name="supportTrackingPhone"
            label={<span className="font-semibold">{t("common.trackingSupportPhone")}</span>}
          >
            <Input placeholder={t("common.trackingSupportPhone")} type="text" />
          </Form.Item>
          <Form.Item
            name="supportTrackingWhatsapp"
            label={<span className="font-semibold">{t("common.trackingSupportWhatsApp")}</span>}
          >
            <Input placeholder={t("common.trackingSupportWhatsApp")} type="text" />
          </Form.Item>
        </div>
        <div className="flex gap-x-4">
          <KosmoButton
            htmlType="submit"
            isDisabled={!isTrackingConfigModified || isLoading}
            isLoading={isLoading}
          >
            {t("common.save")}
          </KosmoButton>
        </div>
      </Form>
    </>
  )
}

export default TrackingConfigForm
